import React, { useEffect, useRef, useState } from "react";
import { getToken } from "../api";
import { track } from "../utils/analytics";

/**
 * ExtensionConnectPage — one-click account link-up for the Chrome extension,
 * at /extension-connect (public route, mounted before App like /irab).
 *
 * Protocol (window.postMessage, same window; the extension's content script
 * relays to its service worker and answers — no extension ID needed):
 *   → { type: "muraja:extension-ping" }
 *   ← { type: "muraja:extension-pong", version, connected, tier }
 *   → { type: "muraja:extension-connect", token }
 *   ← { type: "muraja:extension-connected", ok, tier, error }
 *
 * The token only ever moves within this page's own window — same-origin,
 * user-initiated, and the extension hard-checks the sender origin.
 */
export default function ExtensionConnectPage() {
  const token = getToken();
  // phase: detecting | no-extension | ready | connecting | connected | already | error
  const [phase, setPhase] = useState("detecting");
  const [tier, setTier] = useState(null);
  const [error, setError] = useState("");
  const pongTimer = useRef(null);

  useEffect(() => {
    document.title = "Connect the extension — مراجعة";
    track("extension-connect-view", { loggedIn: !!token });
    const onMessage = (e) => {
      if (e.source !== window || e.origin !== window.location.origin) return;
      const d = e.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "muraja:extension-pong") {
        clearTimeout(pongTimer.current);
        if (d.connected) {
          setTier(d.tier || null);
          setPhase("already");
        } else {
          setPhase("ready");
        }
      } else if (d.type === "muraja:extension-connected") {
        if (d.ok) {
          setTier(d.tier || null);
          setPhase("connected");
          track("extension-connect-success", { tier: d.tier || "unknown" });
        } else {
          setError(d.error || "unknown");
          setPhase("error");
          track("extension-connect-error", { error: d.error || "unknown" });
        }
      } else if (d.type === "muraja:extension-disconnected") {
        setPhase("ready");
      }
    };
    window.addEventListener("message", onMessage);
    // Detect the extension (content scripts inject at document_idle — retry
    // briefly before concluding it isn't installed).
    let tries = 0;
    const ping = () => {
      window.postMessage({ type: "muraja:extension-ping" }, window.location.origin);
      tries += 1;
      if (tries < 6) pongTimer.current = setTimeout(ping, 500);
      else setPhase((p) => (p === "detecting" ? "no-extension" : p));
    };
    ping();
    return () => {
      clearTimeout(pongTimer.current);
      window.removeEventListener("message", onMessage);
    };
  }, [token]);

  const connect = () => {
    setPhase("connecting");
    track("extension-connect-click", {});
    window.postMessage({ type: "muraja:extension-connect", token }, window.location.origin);
  };

  return (
    <div className="dark-scope min-h-screen flex flex-col" style={{ background: "var(--ink)" }}>
      <header className="flex items-center justify-between px-5 py-4 max-w-3xl w-full mx-auto">
        <a href="https://muraja.app" className="flex items-baseline gap-2 no-underline">
          <span className="font-amiri text-2xl text-dt-gold-bright">مراجعة</span>
          <span className="text-xs text-dt-parchment-dim tracking-wide">murājaʿa</span>
        </a>
      </header>

      <main className="flex-1 flex items-start justify-center px-3 sm:px-4 pb-8">
        <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-lg px-6 py-10 text-center mt-6">
          <p className="font-amiri text-3xl text-dt-parchment mb-1">ربط الإضافة</p>
          <p className="text-sm text-dt-parchment-dim mb-8">
            Connect the Chrome extension to your murājaʿa account
          </p>

          {!token ? (
            <>
              <p className="text-sm text-dt-parchment mb-5">
                Sign in to your account first, then come back to this page to connect.
              </p>
              <a
                href="/"
                className="inline-block px-5 py-2.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter] no-underline"
              >
                Sign in
              </a>
            </>
          ) : phase === "detecting" ? (
            <p className="text-sm text-dt-parchment-dim">Looking for the extension…</p>
          ) : phase === "no-extension" ? (
            <>
              <p className="text-sm text-dt-parchment mb-5">
                The Murājaʿa Iʿrāb extension wasn't detected in this browser.
                Install it, then reload this page.
              </p>
              {/* TODO: swap for the Chrome Web Store listing URL after publication */}
              <p className="text-xs text-dt-parchment-dim">
                Extension not published yet? Load it unpacked and reload this page.
              </p>
            </>
          ) : phase === "ready" || phase === "connecting" ? (
            <>
              <p className="text-sm text-dt-parchment mb-6">
                One click and the extension uses your plan's daily allowance —
                AI-checked iʿrāb everywhere you read.
              </p>
              <button
                onClick={connect}
                disabled={phase === "connecting"}
                className="px-6 py-3 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter] disabled:opacity-60"
              >
                {phase === "connecting" ? "Connecting…" : "Connect extension"}
              </button>
            </>
          ) : phase === "connected" || phase === "already" ? (
            <>
              <p className="text-lg text-dt-gold-bright mb-2">
                {phase === "already" ? "Already connected ✓" : "Connected ✓"}
              </p>
              <p className="text-sm text-dt-parchment-dim mb-6">
                {tier === "subscriber"
                  ? "Your subscriber allowance now applies in the extension. You can close this tab."
                  : "Your account is linked. Start a subscription to unlock the bigger daily allowance in the extension."}
              </p>
              <p className="text-xs text-dt-parchment-dim">
                Select any Arabic text on a webpage and tap إعراب to try it.
              </p>
            </>
          ) : (
            <>
              <p className="text-sm text-dt-parchment mb-2">Couldn't connect the extension.</p>
              <p className="text-xs text-dt-parchment-dim mb-6">
                {error === "invalid_token"
                  ? "Your session looks stale — sign out and back in, then retry."
                  : error === "network"
                    ? "The extension couldn't reach the server. Check your connection and retry."
                    : `Unexpected error (${error}). Reload the page and retry.`}
              </p>
              <button
                onClick={connect}
                className="px-5 py-2.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter]"
              >
                Try again
              </button>
            </>
          )}
        </div>
      </main>

      <footer className="px-5 py-4 text-center text-xs text-dt-parchment-dim space-x-3">
        <a href="https://muraja.app" className="hover:text-dt-parchment">muraja.app</a>
        <a href="https://muraja.app/privacy.html" className="hover:text-dt-parchment">Privacy</a>
      </footer>
    </div>
  );
}
